import { Box, Typography, List, ListItem, useTheme } from "@mui/material";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import LockIcon from "@mui/icons-material/Lock";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import whiteLogo from "../assets/white-logo.svg";
import { useEffect, useState } from "react";
import { ROUTES } from "../routes";

const sidebarSections = [
  {
    label: "MAIN",
    items: [
      { name: "Overview", path: ROUTES.overview, Icon: AutoAwesomeOutlinedIcon },
      { name: "Dashboard", path: ROUTES.dashboard, Icon: DashboardOutlinedIcon },
      { name: "Documentation", path: ROUTES.docs, Icon: MenuBookOutlinedIcon },
      { name: "Downloads", path: ROUTES.downloads, Icon: DownloadOutlinedIcon },
    ],
  },
  {
    label: "PLATFORM",
    items: [
      { name: "Devices", path: ROUTES.devices, Icon: DevicesOutlinedIcon },
    ],
  },
  {
    label: "TOOLS",
    items: [
      { name: "Thrust Stand", path: ROUTES.thrustStand, expandable: true, Icon: SpeedOutlinedIcon },
      { name: "Log Analyzer", path: ROUTES.logAnalyzer, Icon: InsightsOutlinedIcon },
      { name: "Propulsion Calc", path: ROUTES.propulsionCalc, Icon: CalculateOutlinedIcon },
      { name: "Firmware Flasher", path: ROUTES.firmwareFlasher, Icon: MemoryOutlinedIcon },
      { name: "Motor Data", path: ROUTES.motorData, Icon: TableChartOutlinedIcon },
    ],
  },
];

const thrustStandItems = [
  { name: "Home", path: ROUTES.thrustStand, exact: true },
  { name: "Thrust Stands", path: `${ROUTES.thrustStand}/thrusts` },
  { name: "Requests", path: `${ROUTES.thrustStand}/requests` },
  { name: "Calculate Thrust", path: `${ROUTES.thrustStand}/calculate` },
  { name: "Visualize History", path: `${ROUTES.thrustStand}/visualize` },
  { name: "Compare Datasets", path: `${ROUTES.thrustStand}/compare` },
];

const Sidebar2 = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const onThrustStandRoute = location.pathname.startsWith(ROUTES.thrustStand);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [thrustStandOpen, setThrustStandOpen] = useState(onThrustStandRoute);

  useEffect(() => {
    if (onThrustStandRoute) setThrustStandOpen(true);
  }, [onThrustStandRoute]);

  const isActive = (path, exact = false) => {
    if (path === "/") return location.pathname === "/";
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const deviceMatch = location.pathname.match(
    /^\/services\/developer\/thrust-stand\/thrusts\/([^/]+)/
  );
  const deviceId = deviceMatch?.[1];
  const deviceItems = deviceId
    ? [
        { name: "Setup", path: `${ROUTES.thrustStand}/thrusts/${deviceId}/setup` },
        {
          name: "Manual Control",
          path: `${ROUTES.thrustStand}/thrusts/${deviceId}/manual-control`,
        },
        { name: "Export", path: `${ROUTES.thrustStand}/thrusts/${deviceId}/export` },
      ]
    : [];

  const handleThrustStandClick = () => {
    if (!onThrustStandRoute) {
      setThrustStandOpen(true);
      navigate(ROUTES.thrustStand);
      return;
    }
    setThrustStandOpen((open) => !open);
  };

  const renderNestedItem = (item, depth = 1) => {
    const active = isActive(item.path, item.exact);
    return (
      <ListItem
        key={item.path}
        component={RouterLink}
        to={item.path}
        sx={{
          textDecoration: "none",
          color: "inherit",
          minHeight: "32px",
          padding: `7px 10px 7px ${depth === 1 ? 26 : 40}px`,
          cursor: "pointer",
          borderRadius: "5px",
          margin: "1px 0",
          background: active ? "rgba(58, 142, 255, 0.09)" : "transparent",
          "&:hover": { background: isDark ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.05)" },
        }}
      >
        <Box
          sx={{
            width: "5px",
            height: "5px",
            borderRadius: "50%",
            background: active ? "#3a8eff" : isDark ? "#31465f" : "#94a3b8",
            marginRight: "9px",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: depth === 1 ? "12px" : "11.5px",
            fontWeight: active ? 500 : 400,
            color: active ? "#3a8eff" : isDark ? "#607895" : "#64748b",
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
        >
          {item.name}
        </Typography>
      </ListItem>
    );
  };

  const COLLAPSED_WIDTH = "42px";
  const EXPANDED_WIDTH = "188px";

  return (
    <div
      style={{
        height: "100vh",
        width: !props.isAuthenticated ? "50px" : sidebarOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        background: theme.palette.canvas.main,
        borderRight: sidebarOpen ? `1px solid ${isDark ? "#111" : "#e2e8f0"}` : "none",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarOpen ? "flex-start" : "center",
          height: "54px",
          padding: sidebarOpen ? "0 18px" : "0",
          borderBottom: `1px solid ${isDark ? "#111" : "#e2e8f0"}`,
          cursor: "pointer",
          flexShrink: 0,
          textDecoration: "none",
          color: "inherit",
        }}
        component={RouterLink}
        to={ROUTES.overview}
      >
        <img
          src={whiteLogo}
          alt="Vecros logo"
          style={{ height: "24px", filter: isDark ? "none" : "invert(1)" }}
        />
      </Box>

      {props.isAuthenticated && sidebarOpen && (
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingBottom: "16px" }}>
          {sidebarSections.map((section) => (
            <Box key={section.label}>
              <Typography
                sx={{
                  fontSize: "10px",
                  color: isDark ? "#3a6080" : "#64748b",
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.1em",
                  padding: "14px 10px 6px 20px",
                  textDecoration: "underline",
                }}
              >
                {section.label}
              </Typography>

              <List sx={{ padding: "0 10px" }}>
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Box key={item.name}>
                      <ListItem
                        {...(item.expandable
                          ? { onClick: handleThrustStandClick }
                          : { component: RouterLink, to: item.path })}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          cursor: "pointer",
                          borderRadius: "8px",
                          margin: "2px 0",
                          borderLeft: active ? "3px solid #3a8eff" : "3px solid transparent",
                          background: active ? "rgba(58, 142, 255, 0.10)" : "transparent",
                          "&:hover": { background: active ? "rgba(58, 142, 255, 0.14)" : isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.05)" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                          {item.Icon && (
                            <item.Icon
                              sx={{
                                fontSize: "18px",
                                color: active ? "#3a8eff" : isDark ? "#5a7090" : "#64748b",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: "13.5px",
                              fontWeight: active ? 500 : 400,
                              color: active ? "#3a8eff" : isDark ? "#5a7090" : "#475569",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                        {item.lock && <LockIcon sx={{ fontSize: "16px", color: "#3a5070" }} />}
                        {item.expandable &&
                          (thrustStandOpen ? (
                            <ExpandLessIcon sx={{ fontSize: "17px", color: isDark ? "#607895" : "#64748b" }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: "17px", color: isDark ? "#607895" : "#64748b" }} />
                          ))}
                      </ListItem>

                      {item.expandable && thrustStandOpen && (
                        <List disablePadding sx={{ margin: "2px 0 6px" }}>
                          {thrustStandItems.map((child) => (
                            <Box key={child.path}>
                              {renderNestedItem(child)}
                              {child.name === "Thrust Stands" && deviceItems.length > 0 && (
                                <Box sx={{ borderLeft: `1px solid ${isDark ? "#18283b" : "#dbe4ee"}`, marginLeft: "28px" }}>
                                  {deviceItems.map((deviceItem) => renderNestedItem(deviceItem, 2))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </List>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}

      {props.isAuthenticated && (
        <Box
          onClick={() => setSidebarOpen((open) => !open)}
          sx={{
            position: sidebarOpen ? "static" : "absolute",
            bottom: sidebarOpen ? "auto" : "16px",
            left: sidebarOpen ? "auto" : "10px",
            display: "flex",
            justifyContent: sidebarOpen ? "flex-end" : "center",
            padding: sidebarOpen ? "12px 10px" : "0",
            color: isDark ? "#8899bb" : "#64748b",
            cursor: "pointer",
            "&:hover": { color: "#fff" },
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </Box>
      )}
    </div>
  );
};

export default Sidebar2;
